import { useEffect, Suspense, lazy } from 'react'
import { Route } from 'react-router-dom'
import axios from 'axios'
import { reaction } from 'mobx'
import { toast } from 'react-toastify'
import debounce from 'lodash/debounce'

import Modals from 'singletons/Modals'
import HiddenSettings from 'singletons/HiddenSettings'
import User from 'singletons/User'
import Pusher from 'singletons/Pusher'
import GlobalEvents from 'singletons/GlobalEvents'
import getDeepestData from 'util/getDeepestData'
import parseBackendErrors from 'util/parseBackendErrors'
import { AUTH, DASHBOARD, SSO_ENTRY } from 'constants/routing'
import { AUTO_LOGOUT_MODAL_MINUTES_DELAY } from 'constants/session'

import { default as fastFormsConfigure } from '@ui-components-3/fast-forms/lib/utils/configure'
import makeQuery from 'api/makeQuery'
import SsoWelcome from 'components/Entry/SsoWelcome'
import { isSuccessVisit } from 'constants/visit-types'
import { APMRoutes } from 'util/apm'

const ignoredUnknownErrors = [
  /** Error occurs from @floating-ui/react in ui-comp-3 <Select/> when used inside drawer (only when menu height would overflow doc, due to iten count).
   * Per lib's creator, error is harmless and can be ignored:
   * https://github.com/floating-ui/floating-ui/issues/1740#issuecomment-1563544849
   */
  'ResizeObserver loop completed with undelivered notifications.',
]

// TODO: Normally this should be placed inside app.js but circular dep error from makeQuery-User-Providers needs to be resolved.
fastFormsConfigure({
  fetchForms: (formGroups, extraParams) => {
    const filter = { form_group: formGroups[0] }
    // TODO: maybe add switch to attach different filter values per form_group
    if (extraParams.version) {
      filter.version = extraParams.version
    }

    if (!extraParams.isSuccess) {
      filter.group_section = { $ne: 'success_coaching' }
    }

    return makeQuery('getForms', { filter })
  },
  fetchFormResponses: (formGroups, targetIds, extraParams) => {
    switch (extraParams?.type) {
      case 'protocols': {
        if (targetIds) {
          const { groupId, clientId } = targetIds
          const filter = {
            client_id: clientId,
            group_id: groupId,
            form_group: formGroups[0],
          }
          if (extraParams.sendActive) {
            filter.active = true
          }

          return makeQuery('getCareProtocols', {
            filter,
          })
        }
        break
      }
      case 'safety_plan': {
        if (targetIds) {
          const { pendingVisitId } = targetIds
          const visit = User.provider?.visitQueue.getById(pendingVisitId)
          if (visit?.safetyPlan?.form) {
            return [
              {
                form: visit.safetyPlan.form,
              },
            ]
          }
        }
        return []
      }
      // SafetyPlan presence varies when viewing past visits page vs. Visit>visitsTab, so passing as extra param for consistency when "fetching" form responses bc it is present in component calling fetchFormGroups
      case 'safety_plan_past': {
        if (extraParams.safetyPlan) {
          return [
            {
              form: extraParams.safetyPlan.form,
            },
          ]
        }
        return []
      }
    }
    throw new Error('Wrong responses')
  },
  submitForm: async (formBase, targetIds, extraParams) => {
    switch (extraParams?.type) {
      case 'safety_plan': {
        if (targetIds) {
          const { visitId, pendingVisitId } = targetIds
          const form = {
            formId: formBase.form.formId,
            answers: formBase.answers,
            // for by field submission switch to answers: [{fieldId: formBase.fieldId value: formBase.value }]
          }
          const data = await makeQuery('putSafetyPlan', {
            form,
            visitId,
          })
          const visit = User.provider?.visitQueue.getById(pendingVisitId)
          visit.merge(data)
          return {
            form: data?.safetyPlan?.form,
          }
        }
      }
    }
    throw new Error('No submit method for this type of form')
  },
})

const addCountIfExists = (msg, e) => {
  const count = e.meta?.count || 0
  return count ? `${msg} (${count})` : msg
}

const upsertToast = (toastId, type, msg, options) =>
  toast.isActive(toastId)
    ? toast.update(toastId, {
        type,
        render: msg,
        ...options,
      })
    : toast(msg, {
        toastId,
        type,
        ...options,
      })

const DashboardContainer = lazy(() => import('components/Dashboard'))
const EntryContainer = lazy(() => import('components/Entry'))

const RouterTree = () => {
  useEffect(() => {
    const openModal = debounce(
      () => Modals.some((m) => m.type === 'autoLogout') || Modals.open('autoLogout'),
      AUTO_LOGOUT_MODAL_MINUTES_DELAY * 1000 * 60,
    )

    const deactivate = () => {
      openModal.cancel()
      window.removeEventListener('pointermove', openModal)
      window.removeEventListener('pointerdown', openModal)
    }

    const activate = () => {
      window.addEventListener('pointermove', openModal)
      window.addEventListener('pointerdown', openModal)
      openModal()
    }

    const dispose = reaction(
      () => User.needsLogin || User.currentVideo?.active,
      (shouldDeactivate) => {
        if (shouldDeactivate) {
          deactivate()
        } else {
          activate()
        }
      },
      { fireImmediately: true },
    )

    return () => {
      dispose()
      deactivate()
    }
  }, [])

  useEffect(
    () =>
      reaction(
        () => User.provider?.isMedical && HiddenSettings.isMedicalRedirectEnabled,
        (shouldRedirect) => {
          if (shouldRedirect) {
            User.logout()
            Modals.open('preventMedicalProvider')
          }
        },
      ),
    [],
  )

  useEffect(
    () =>
      Pusher.listen((e) => {
        switch (e.event) {
          /* TODO: when dismissing error, this also fires. Maybe show 'Dismissed'? */
          case 'rx-error':
            upsertToast('rx-error', toast.TYPE.ERROR, addCountIfExists('New RX transmission error', e), {
              autoClose: false,
            })
            break
          case 'rx-change':
            upsertToast('rx-change', toast.TYPE.INFO, addCountIfExists('New RX change request received', e), {
              autoClose: false,
            })
            break
        }
      }),
    [],
  )

  /*
    Versions of react router's history and the one we install manually may be different.
    If we use createBrowserHistory, we will use history version react router is not suited with.
    So it is better to get history by hook, it could cause less bugs.
  */
  useEffect(() => {
    const ejectId = axios.interceptors.response.use(null, (e) => {
      switch (e.response?.status) {
        case 401: {
          User.logout()
          break
        }
        case 403: {
          if (!e.config?.shouldNotLogoutOn403Error) User.logout()
          break
        }
      }

      /* MUST rethrow */
      return Promise.reject(getDeepestData(e.response) || e.message)
    })

    return () => axios.interceptors.response.eject(ejectId)
  }, [])

  useEffect(
    () =>
      GlobalEvents.listen((type, data) => {
        const isSuccess = isSuccessVisit(data?.visitType)
        const visitTitle = isSuccess ? 'Session' : 'Visit'

        switch (type) {
          case 'unknownError':
            if (!ignoredUnknownErrors.includes(data.printedError)) {
              toast.error(data.printedError, { toastId: data.printedError })
            }

            break
          case 'updateProviderSchedule':
            data.status === 'success'
              ? toast.success('Schedule was updated successfully')
              : toast.error('Failed to update schedule')
            break
          case 'refetchCollections':
            toast.info('One of the appointments has become active, collections have been synchronized')
            break
          case 'confirmSchedulingVisit':
            if (data.status === 'success') {
              toast.success(`Scheduling with ${data.memberFullName} has been added to your calendar`)
            }
            break
          case 'sendProviderVerification':
            if (data.status === 'success') {
              toast.success('Verification code has been sent to your phone')
            }
            break
          case 'startPendingVisit':
            if (data.status === 'success') {
              toast.success(`${visitTitle} started`)
            }
            break
          case 'forgotPassword':
            data.status === 'success'
              ? toast.success(`Reset password email has been sent to ${data.email}`)
              : toast.error(`Error sending reset password email to ${data.email}`)
            break
          case 'cancelPendingVisit':
            if (data.status === 'success') {
              toast.success(`${visitTitle} with ${data.memberFullName} was cancelled. Reason: ${data.reason}`)
            }
            break
          case 'saveVisitForLater':
            data.status === 'success'
              ? toast.success(`${visitTitle} is now in review.`)
              : toast.error(`Error updating ${visitTitle.toLowerCase()}.`)
            break
          case 'confirmRxChangeRequest':
            if (data.status === 'success') {
              toast.success('Change request approved successfully')
            } else {
              const err = parseBackendErrors(data.e)
              toast.error(`Failed to approve change request${err && `: ${err}`}`)

              if (process.env.NODE_ENV !== 'production') {
                console.warn('error', data.e)
              }
            }
            break
          case 'denyRxChangeRequest':
            if (data.status === 'success') {
              toast.success('Change request denied successfully')
            } else {
              const err = parseBackendErrors(data.e)
              toast.error(`Failed to deny change request${err && `: ${err}`}`)

              if (process.env.NODE_ENV !== 'production') {
                console.warn('error', data.e)
              }
            }
            break
          case 'verifyProviderCode':
            if (data.status === 'success') {
              toast.success('The code was successfully verified')
            }
            break
          case 'rejectProviderSchedulings':
            if (data.status === 'success') {
              toast.success('New times sent successfully')
            }
            break
          case 'cancelProviderSchedulings':
            if (data.status === 'success') {
              toast.success(`Scheduling ${data.memberFullName} was cancelled. Reason: ${data.reason}`)
            }
            break
          case 'visitStartVideo':
            if (data.status === 'error') {
              toast.error(data.description)
            }
            break
          case 'visitStartPhone':
            if (data.status === 'error') {
              Modals.open('phoneFallback', { visit: data.visit, error: data?.error })
            } else if (data.status === 'success') {
              Modals.open('phoneSuccess')
            }
            break
          case 'addPrescription':
            if (data.status === 'error') {
              toast.error(
                `${data.name} is a controlled substance
                  which cannot be prescribed on the Timely platform at this time`,
              )
            }
            break
          case 'updateUserPassword':
            if (data.status === 'success') {
              toast.success('Password was successfully updated')
            }
            break
          case 'updateProvider':
            data.status === 'success'
              ? toast.success('Provider was updated successfully')
              : toast.error('Failed to update provider')
            break
          case 'ssoLogin':
            if (data.status === 'error') {
              toast.error('Failed to fetch your information')
            }
            break
          case 'mfaTokenExpiration':
            if (data.status === 'error') {
              toast.error('Your MFA session has expired. Please re-enter your login credentials to try again.')
            }
            break
        }
      }),
    [],
  )

  return (
    <Suspense fallback="Loading...">
      <APMRoutes>
        <Route path={`${AUTH}/*`} element={<EntryContainer />} />
        <Route path={`${DASHBOARD}*`} element={<DashboardContainer />} />
        <Route path={SSO_ENTRY} element={<SsoWelcome />} />
      </APMRoutes>
    </Suspense>
  )
}

export default RouterTree
